import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Table, Popconfirm, message, Tabs } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import moment from "moment";
import request from "helper/request";
import RoutesHeader from "modules/client/routes/components/header/RoutesHeader";
import FilterScan2Pack from "../components/FilterScan2Pack";
import Scan2PackReport from "../components/Scan2PackReport";
import StatusTag from "../components/StatusTag";
import { DATE_FORMAT_FRONTEND } from "configs/constants";
import { shortId, convertArrayToObject } from "helper/util";
import { ProfileContext } from "layouts/private/ProfileContext";
import { selectCurrentUser } from "../../../auth/store/authSlice";
import { doOptimizeOrder, doStoreDraftId } from "../../active-orders/store/thunks";
import { saveAsDraftNewOrder } from "../../active-orders/store/orderSlice";
import {
  dataJobTypeOptimize,
  handleRoutesOptimize,
  deleteShiftStart
} from "../../active-orders/helper";
import { getVehicleTypeList } from "../../vehicle-types/store/thunks";
import "./Scan2PackPage.scss";

const { TabPane } = Tabs;

const Scan2PackPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const profileCtx = useContext(ProfileContext);
  const user = useSelector(selectCurrentUser);

  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingPlanId, setSubmittingPlanId] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({});
  const [activeTab, setActiveTab] = useState("totes");

  const breadCrumbs = [{ text: "SCAN 2 PACK" }];

  // createdDate comes from the DatePicker as a moment or a DD/MM/YYYY string
  const createdDateFilter = filter.createdDate
    ? moment(filter.createdDate, DATE_FORMAT_FRONTEND)
    : null;

  const filteredPlans = allPlans.filter((p) => {
    if (filter.id && !p.id?.toLowerCase().includes(filter.id.toLowerCase())) {
      return false;
    }
    if (filter.status && p.status !== filter.status) {
      return false;
    }
    if (createdDateFilter) {
      if (!p.createdAt || !moment(p.createdAt).isSame(createdDateFilter, "day")) {
        return false;
      }
    }
    if (filter.packageId) {
      const search = filter.packageId.toLowerCase();
      const hasPackage = (p.paquetes || []).some(
        (paquete) =>
          paquete.id?.toLowerCase().includes(search) ||
          paquete.barcodeId?.toLowerCase().includes(search)
      );
      if (!hasPackage) return false;
    }
    if (filter.packerName) {
      const search = filter.packerName.toLowerCase();
      const driver = p.driver;
      const name = [driver?.firstName, driver?.lastName].filter(Boolean).join(" ");
      const matches =
        name.toLowerCase().includes(search) ||
        driver?.email?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const routePlans = filteredPlans.filter((p) => !p.toteId);
  const totes = filteredPlans.filter((p) => !!p.toteId);
  // Reporting aggregates over all totes, ignoring the filter panel
  const allTotes = allPlans.filter((p) => !!p.toteId);

  useEffect(() => {
    fetchAll();
    dispatch(getVehicleTypeList());
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const response = await request.get("/routeplan");
      setAllPlans(response || []);
    } catch (error) {
      console.error("Error fetching scan2pack data:", error);
      message.error("Failed to load Scan 2 Pack data");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    try {
      await request.delete(`/routeplan/${planId}`);
      message.success("Plan deleted successfully");
      fetchAll();
    } catch (error) {
      console.error("Error deleting plan:", error);
      message.error("Failed to delete plan");
    }
  };

  const handleViewPlan = (planId) => {
    history.push(`/routeplan/${planId}`);
  };

  const handleViewTote = (toteRecordId) => {
    history.push(`/tote/${toteRecordId}`);
  };

  // Keep the filter panel open on apply — collapsing it causes a jarring jump
  const handleClickFilter = (values) => {
    setFilter(values);
  };

  const handleResetFilter = () => {
    setFilter({});
  };

  // From the Reporting tab: apply the day/status filter and jump to the Totes tab
  const handleViewTotesFromReport = (values) => {
    setFilter(values);
    setShowFilter(true);
    setActiveTab("totes");
  };

  const updatePlanStatus = async (planId, status) => {
    try {
      await request.patch(`/routeplan/${planId}`, { status });
    } catch (error) {
      console.warn("Could not update plan status:", error);
    }
  };

  const generateRandomJobId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let result = "";
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `job_${result}`;
  };

  const validateCoordinates = (lat, lng, addressString) => {
    const DEFAULT_LAT = 18.4134459;
    const DEFAULT_LNG = -66.098316;
    const isInvalid = !lat || !lng || lat === 0 || lng === 0;
    if (isInvalid) {
      console.warn(
        `⚠️ Invalid coordinates for address: ${addressString}. Using default.`
      );
      return { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
    }
    return { lat, lng };
  };

  const handleSubmitPlan = async (planId) => {
    try {
      setSubmittingPlanId(planId);
      await updatePlanStatus(planId, "submitting");
      message.loading({
        content: "Preparing route for optimization...",
        key: "submit-plan"
      });

      const planDetails = await request.get(`/routeplan/${planId}`);

      if (!planDetails || !planDetails.paquetes || planDetails.paquetes.length === 0) {
        message.error("Cannot submit plan: No packages found");
        await updatePlanStatus(planId, "failed");
        setSubmittingPlanId(null);
        return;
      }

      const buildAddressString = (addr) => {
        if (!addr) return "";
        return [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.zipCode]
          .filter(Boolean)
          .join(", ");
      };

      const addressesToGeocode = new Map();
      for (const paquete of planDetails.paquetes) {
        const pickupAddr = buildAddressString(paquete.pickupAddress);
        const deliveryAddr = buildAddressString(paquete.deliveryAddress);
        if (pickupAddr && !paquete.pickupAddress?.latitude) {
          addressesToGeocode.set(pickupAddr, { type: "pickup" });
        }
        if (deliveryAddr && !paquete.deliveryAddress?.latitude) {
          addressesToGeocode.set(deliveryAddr, { type: "delivery" });
        }
      }

      message.loading({ content: "Geocoding addresses...", key: "submit-plan" });
      const geocodedMap = {};
      for (const [address] of addressesToGeocode.entries()) {
        try {
          const result = await request.get("/geocode", { params: { address } });
          geocodedMap[address] =
            result?.lat && result?.lng
              ? { lat: result.lat, lng: result.lng }
              : { lat: 0, lng: 0 };
        } catch {
          geocodedMap[address] = { lat: 0, lng: 0 };
        }
      }

      const formattedJobs = planDetails.paquetes.map((paquete) => {
        const pickupStr = buildAddressString(paquete.pickupAddress);
        const deliveryStr = buildAddressString(paquete.deliveryAddress);

        const rawPickup = paquete.pickupAddress?.latitude
          ? { lat: paquete.pickupAddress.latitude, lng: paquete.pickupAddress.longitude }
          : geocodedMap[pickupStr] || { lat: 0, lng: 0 };

        const rawDelivery = paquete.deliveryAddress?.latitude
          ? {
              lat: paquete.deliveryAddress.latitude,
              lng: paquete.deliveryAddress.longitude
            }
          : geocodedMap[deliveryStr] || { lat: 0, lng: 0 };

        const pickupCoords = validateCoordinates(rawPickup.lat, rawPickup.lng, pickupStr);
        const deliveryCoords = validateCoordinates(
          rawDelivery.lat,
          rawDelivery.lng,
          deliveryStr
        );
        const jobId = generateRandomJobId();

        return {
          id: jobId,
          pickup: {
            address: pickupStr,
            lat: pickupCoords.lat,
            lng: pickupCoords.lng,
            start: "06:00",
            end: "20:00",
            duration: 10
          },
          dropoff: {
            address: deliveryStr,
            lat: deliveryCoords.lat,
            lng: deliveryCoords.lng,
            start: "06:00",
            end: "20:00",
            duration: 10
          },
          packages: [
            {
              id: `${jobId}-package`,
              quantity: 1,
              name: "Small",
              packageTypeId: "1844330d-167d-4dd9-948b-b8a6bee7dd3b",
              maxLength: 12,
              maxWeight: 10,
              maxVolume: 1
            }
          ]
        };
      });

      const visits = dataJobTypeOptimize(formattedJobs);
      const clientId = profileCtx?.profile?.clientId;
      if (clientId) {
        Object.keys(visits).forEach((jobId) => {
          visits[jobId].tags = [`client_id_${clientId}`];
        });
      }

      const numberRoutes = 1;
      const pickupsForRedux = formattedJobs.map((job, index) => ({
        id: `pickup-${index}`,
        title: "Pickup Location",
        position: index + 1,
        address: {
          address: job.pickup.address,
          lngLat: [job.pickup.lng, job.pickup.lat]
        },
        from_time: moment(job.pickup.start, "HH:mm"),
        to_time: moment(job.pickup.end, "HH:mm"),
        duration: job.pickup.duration,
        phone_numbers: [{ type: "contact", phone: "9396521963" }],
        contact_name: "Route Contact"
      }));

      const dropoffsForRedux = formattedJobs.map((job, index) => ({
        id: `dropoff-${index}`,
        title: "Drop-off Location",
        position: index + 1,
        address: {
          address: job.dropoff.address,
          lngLat: [job.dropoff.lng, job.dropoff.lat]
        },
        from_time: moment(job.dropoff.start, "HH:mm"),
        to_time: moment(job.dropoff.end, "HH:mm"),
        duration: job.dropoff.duration,
        phone_numbers: [{ type: "contact", phone: "9396521963" }],
        contact_name: "Route Contact"
      }));

      const packagesForRedux = formattedJobs.map((job, index) => ({
        id: `package-${index}`,
        pickupId: `pickup-${index}`,
        packageTypeId: "1844330d-167d-4dd9-948b-b8a6bee7dd3b",
        quantity: 1,
        name: "Small",
        type: "Small",
        maxLength: 12,
        maxWeight: 10,
        maxVolume: 1,
        dropoffs: [{ id: `dropoff-${index}`, quantity: 1 }]
      }));

      const orderData = {
        jobs: convertArrayToObject(formattedJobs, "id"),
        pickups: [
          {
            address: {
              address: formattedJobs[0]?.pickup?.address || "",
              lngLat: [
                formattedJobs[0]?.pickup?.lng || 0,
                formattedJobs[0]?.pickup?.lat || 0
              ]
            }
          }
        ],
        clientId: clientId
      };

      const fleetParam = deleteShiftStart(
        handleRoutesOptimize(orderData, numberRoutes, [])
      );

      dispatch(
        saveAsDraftNewOrder({
          data: {
            ...orderData,
            routes: fleetParam,
            pickups: pickupsForRedux,
            dropoffs: dropoffsForRedux,
            packages: packagesForRedux,
            isOptimizeRoute: true,
            isConfirmLocations: true,
            isConfirmRoute: false,
            clientId: clientId || "",
            planId: planId,
            fromPlanFlow: true
          }
        })
      );

      message.loading({ content: "Submitting for optimization...", key: "submit-plan" });
      const draftId = new Date().getTime();
      dispatch(doStoreDraftId(draftId));

      await dispatch(
        doOptimizeOrder({
          userId: user?.sub,
          draftId,
          visits,
          fleet: fleetParam,
          options: {
            polylines: true,
            balance: true,
            distance_type: "distance_travelled"
          },
          order: { ...orderData, routes: fleetParam }
        })
      );

      message.success({
        content: "Navigating to confirmation page...",
        key: "submit-plan",
        duration: 1
      });
      setTimeout(() => {
        history.push("/orders/new");
      }, 100);
    } catch (error) {
      console.error("Error submitting plan:", error);
      await updatePlanStatus(planId, "failed");
      message.error({ content: "Failed to submit plan", key: "submit-plan" });
      setSubmittingPlanId(null);
      fetchAll();
    }
  };

  const routePlanColumns = [
    {
      title: "Plan ID",
      dataIndex: "id",
      key: "id",
      render: (id) => shortId(id)
    },
    {
      title: "Packages",
      key: "packages",
      render: (_, record) => record.paquetes?.length || 0
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-")
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleViewPlan(record.id)}
            size="small"
          >
            View
          </Button>
          {record.status === "failed" ? (
            <Button
              type="default"
              style={{
                backgroundColor: "#ff4d4f",
                color: "#fff",
                borderColor: "#ff4d4f"
              }}
              icon={<ReloadOutlined />}
              onClick={() => handleSubmitPlan(record.id)}
              size="small"
              loading={submittingPlanId === record.id}
              disabled={submittingPlanId !== null && submittingPlanId !== record.id}
            >
              Resubmit
            </Button>
          ) : (
            <Button
              type="default"
              style={{
                backgroundColor: "#52c41a",
                color: "#fff",
                borderColor: "#52c41a"
              }}
              icon={<SendOutlined />}
              onClick={() => handleSubmitPlan(record.id)}
              size="small"
              loading={submittingPlanId === record.id}
              disabled={submittingPlanId !== null || record.status === "submitting"}
            >
              Submit
            </Button>
          )}
          <Popconfirm
            title="Are you sure you want to delete this plan?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ];

  const toteColumns = [
    {
      title: "Tote ID",
      dataIndex: "toteId",
      key: "toteId",
      render: (id) => <strong>{id}</strong>
    },
    {
      title: "Pickup",
      dataIndex: "totePickup",
      key: "totePickup",
      render: (addr) => addr || "-"
    },
    {
      title: "Delivery",
      dataIndex: "toteDelivery",
      key: "toteDelivery",
      render: (addr) => addr || "-"
    },
    {
      title: "Packages",
      key: "packages",
      render: (_, record) => record.paquetes?.length || 0
    },
    {
      title: "Packer Name",
      key: "packerName",
      render: (_, record) => {
        const driver = record.driver;
        if (!driver) return "-";
        const name = [driver.firstName, driver.lastName].filter(Boolean).join(" ");
        return name || driver.email || "-";
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-")
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewTote(record.id)}
          size="small"
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div className="scan2pack-page">
      <RoutesHeader
        breadCrumbs={breadCrumbs}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
      {showFilter && (
        <FilterScan2Pack
          defaultValues={filter}
          setShowFilter={setShowFilter}
          handleClickFilter={handleClickFilter}
          onReset={handleResetFilter}
        />
      )}
      <div className="content-wrapper">
        <h2 className="page-title">Scan 2 Pack</h2>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`Totes (${totes.length})`} key="totes">
            <Table
              dataSource={totes}
              columns={toteColumns}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} totes`
              }}
            />
          </TabPane>
          <TabPane tab={`Route Plans (${routePlans.length})`} key="route-plans">
            <Table
              dataSource={routePlans}
              columns={routePlanColumns}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} plans`
              }}
            />
          </TabPane>
          <TabPane tab="Reporting" key="reporting">
            <Scan2PackReport totes={allTotes} onViewTotes={handleViewTotesFromReport} />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default Scan2PackPage;
